import type { Plan, CurrentSubscription } from './types';
import { DEFAULT_PLANS } from './types';

/** Matches webbuilder-api `plans.ts` when user has no paid subscription. */
export const FREE_TIER_LIMITS = { maxSites: 1, maxPagesPerSite: 5 } as const;

/**
 * Resolves the Free tier for messaging when `GET /subscriptions/plans` may omit a FREE row.
 */
export function getFreeTierFromPlans(plans: Plan[]): {
  maxSites: number;
  maxPagesPerSite: number;
  displayName: string;
  plan: Plan;
} {
  const found = plans.find(p => p.name?.toUpperCase() === 'FREE');
  if (found) {
    return {
      maxSites: coalesceNumber(found.maxSites, FREE_TIER_LIMITS.maxSites),
      maxPagesPerSite: coalesceNumber(found.maxPagesPerSite, FREE_TIER_LIMITS.maxPagesPerSite),
      displayName: found.displayName,
      plan: found,
    };
  }
  const d = DEFAULT_PLANS[0];
  return {
    maxSites: d.maxSites,
    maxPagesPerSite: d.maxPagesPerSite,
    displayName: d.displayName,
    plan: d,
  };
}

function coalesceNumber(n: number | undefined, fallback: number): number {
  if (n === undefined || Number.isNaN(Number(n))) return fallback;
  return Number(n);
}

/** API/UI convention: -1 or very large cap means effectively unlimited. */
export function isUnlimitedPages(n: number): boolean {
  return n < 0 || n >= 999;
}

export function formatPagesPerSiteLabel(n: number): string {
  if (isUnlimitedPages(n)) return 'unlimited';
  return String(n);
}

export function formatSitesLabel(n: number): string {
  if (n === 1) return '1 site';
  return `${n} sites`;
}

/**
 * Shown in downgrade modals when API usage suggests over target site cap (before live wizard load).
 */
export function buildDowngradeUsageNote(
  subscription: CurrentSubscription | null,
  targetMaxSites: number
): string | null {
  const sites = subscription?.usage?.sites;
  if (typeof sites !== 'number' || targetMaxSites <= 0) return null;
  if (sites <= targetMaxSites) return null;
  return `You currently have ${sites} sites; the plan you are moving to allows ${targetMaxSites}. You will be asked to choose which sites to remove before the change can continue.`;
}

function effectivePlanPrice(plan: Plan | null | undefined, allPlans: Plan[]): number {
  if (!plan) return 0;
  const m = Number(plan.priceMonthly);
  if (Number.isFinite(m)) return m;
  const match = allPlans.find(p => p.name?.toUpperCase() === plan.name?.toUpperCase());
  if (match) return Number(match.priceMonthly) || 0;
  const u = plan.name?.toUpperCase() ?? '';
  if (u === 'PROFESSIONAL') return 99;
  if (u === 'STARTER') return 29;
  return 0;
}

/**
 * Classifies selecting `target` relative to the user's `current` plan (by monthly price / tier).
 */
export function comparePlanChange(
  current: Plan | null | undefined,
  target: Plan,
  allPlans: Plan[]
): 'upgrade' | 'downgrade' | 'same' {
  const a = effectivePlanPrice(current ?? null, allPlans);
  const b = effectivePlanPrice(target, allPlans);
  if (b > a) return 'upgrade';
  if (b < a) return 'downgrade';
  return 'same';
}

export function buildUpgradeSuccessMessage(plan: Plan): string {
  const pps = formatPagesPerSiteLabel(plan.maxPagesPerSite);
  const ppsText = pps === 'unlimited' ? 'unlimited pages per site' : `up to ${pps} pages per site`;
  const sitesText = plan.maxSites === 1 ? '1 site' : `up to ${plan.maxSites} sites`;
  return `Congratulations! You are now on the ${plan.displayName} plan, with ${sitesText} and ${ppsText}.`;
}

export function buildDowngradeSuccessMessage(plan: Plan): string {
  const pps = formatPagesPerSiteLabel(plan.maxPagesPerSite);
  const ppsText =
    pps === 'unlimited'
      ? 'unlimited pages per site'
      : `up to ${pps} pages and blog posts per site combined`;
  const sitesText = plan.maxSites === 1 ? '1 site' : `up to ${plan.maxSites} sites`;
  return `Your plan has been changed to ${plan.displayName} (${sitesText} and ${ppsText}).`;
}

export function buildNeutralPlanChangeMessage(plan: Plan): string {
  return `Your plan has been updated to ${plan.displayName}.`;
}
